import React, { useState, useEffect } from 'react';

const ProjectSchedule = ({ projectId, shareToken }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewEventForm, setShowNewEventForm] = useState(false);
  const [newEvent, setNewEvent] = useState({
    eventTitle: '',
    eventDescription: '',
    eventType: 'milestone',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    location: '',
    assignedTo: '',
    assignedToName: '',
    assignedToType: '',
    visibility: 'all',
    participants: []
  });
  const [availableParticipants, setAvailableParticipants] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const canEditSchedule = !shareToken;

  const API_BASE_URL = process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

  useEffect(() => {
    loadEvents();
    loadParticipants();
  }, [projectId]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const url = shareToken
        ? `${API_BASE_URL}/schedule/${projectId}?shareToken=${shareToken}`
        : `${API_BASE_URL}/schedule/${projectId}`;

      const headers = {
        'Content-Type': 'application/json'
      };

      if (token && !shareToken) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, { headers });
      if (!response.ok) throw new Error('Failed to load schedule');
      const data = await response.json();
      setEvents(data.events || []);
    } catch (error) {
      console.error('Error loading schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadParticipants = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const url = shareToken
        ? `${API_BASE_URL}/messages/${projectId}/recipients?shareToken=${shareToken}`
        : `${API_BASE_URL}/messages/${projectId}/recipients`;

      const headers = {
        'Content-Type': 'application/json'
      };

      if (token && !shareToken) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, { headers });
      if (!response.ok) throw new Error('Failed to load participants');
      const data = await response.json();
      setAvailableParticipants(data.recipients || []);
    } catch (error) {
      console.error('Error loading participants:', error);
    }
  };

  const handleCreateEvent = async () => {
    if (!newEvent.eventTitle || !newEvent.startDate) {
      alert('Event title and start date are required');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const url = shareToken
        ? `${API_BASE_URL}/schedule/${projectId}?shareToken=${shareToken}`
        : `${API_BASE_URL}/schedule/${projectId}`;

      const headers = {
        'Content-Type': 'application/json'
      };

      if (token && !shareToken) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const body = {
        ...newEvent,
        participants: selectedParticipants.map(p => {
          const participant = availableParticipants.find(ap => (ap.email || ap.type) === p);
          return {
            type: participant?.type || 'client',
            email: participant?.email || null,
            name: participant?.label || p
          };
        })
      };

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error('Failed to create event');
      
      setNewEvent({
        eventTitle: '',
        eventDescription: '',
        eventType: 'milestone',
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
        location: '',
        assignedTo: '',
        assignedToName: '',
        assignedToType: '',
        visibility: 'all',
        participants: []
      });
      setSelectedParticipants([]);
      setShowNewEventForm(false);
      loadEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Failed to create event');
    }
  };

  const formatEventDate = (date, time) => {
    if (!date) return '';
    const d = new Date(date);
    let formatted = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    if (time) {
      formatted += ` at ${time}`;
    }
    return formatted;
  };

  const getEventTypeColor = (type) => {
    const colors = {
      milestone: 'bg-blue-100 text-blue-700',
      meeting: 'bg-green-100 text-green-700',
      deadline: 'bg-red-100 text-red-700',
      installation: 'bg-purple-100 text-purple-700',
      delivery: 'bg-yellow-100 text-yellow-700',
      other: 'bg-gray-100 text-gray-700'
    };
    return colors[type] || colors.other;
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-gray-100 text-gray-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return colors[status] || colors.scheduled;
  };

  // Group events by date
  const groupedEvents = events.reduce((acc, event) => {
    const date = event.start_date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(event);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedEvents).sort();

  // Calendar view helpers
  const getCalendarDays = () => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    // Get first day of month and number of days
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Normalize event dates to YYYY-MM-DD format for matching
    const normalizedEvents = events.reduce((acc, event) => {
      if (event.start_date) {
        // Handle different date formats
        let dateStr = event.start_date;
        if (dateStr.includes('T')) {
          dateStr = dateStr.split('T')[0];
        }
        if (!acc[dateStr]) {
          acc[dateStr] = [];
        }
        acc[dateStr].push(event);
      }
      return acc;
    }, {});
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      days.push({
        day,
        date: dateStr,
        events: normalizedEvents[dateStr] || []
      });
    }
    
    return days;
  };

  const getMonthName = () => {
    const today = new Date();
    return today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-semibold">Project Schedule</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode(viewMode === 'list' ? 'calendar' : 'list')}
              className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
            >
              {viewMode === 'list' ? '📅 Calendar' : '📋 List'}
            </button>
            {canEditSchedule ? (
              <button
                onClick={() => setShowNewEventForm(!showNewEventForm)}
                className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
              >
                {showNewEventForm ? 'Cancel' : '+ New Event'}
              </button>
            ) : (
              <span className="text-xs text-gray-500 self-center">
                To request changes, message the designer in Trades Chat.
              </span>
            )}
          </div>
        </div>

        {/* New Event Form */}
        {showNewEventForm && (
          <div className="mt-3 space-y-2 p-3 bg-gray-50 rounded-lg">
            <input
              type="text"
              placeholder="Event Title *"
              value={newEvent.eventTitle}
              onChange={(e) => setNewEvent({ ...newEvent, eventTitle: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            
            <textarea
              placeholder="Description (optional)"
              value={newEvent.eventDescription}
              onChange={(e) => setNewEvent({ ...newEvent, eventDescription: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm min-h-[60px]"
            />

            <div className="grid grid-cols-2 gap-2">
              <select
                value={newEvent.eventType}
                onChange={(e) => setNewEvent({ ...newEvent, eventType: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="milestone">Milestone</option>
                <option value="meeting">Meeting</option>
                <option value="deadline">Deadline</option>
                <option value="installation">Installation</option>
                <option value="delivery">Delivery</option>
                <option value="other">Other</option>
              </select>

              <select
                value={newEvent.visibility}
                onChange={(e) => setNewEvent({ ...newEvent, visibility: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">Visible to All</option>
                <option value="designer_only">Designer Only</option>
                <option value="designer_and_client">Designer & Client</option>
                <option value="designer_and_trades">Designer & Trades</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-600">Start Date *</label>
                <input
                  type="date"
                  value={newEvent.startDate}
                  onChange={(e) => setNewEvent({ ...newEvent, startDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">End Date (optional)</label>
                <input
                  type="date"
                  value={newEvent.endDate}
                  onChange={(e) => setNewEvent({ ...newEvent, endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-600">Start Time (optional)</label>
                <input
                  type="time"
                  value={newEvent.startTime}
                  onChange={(e) => setNewEvent({ ...newEvent, startTime: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">End Time (optional)</label>
                <input
                  type="time"
                  value={newEvent.endTime}
                  onChange={(e) => setNewEvent({ ...newEvent, endTime: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </div>

            <input
              type="text"
              placeholder="Location (optional)"
              value={newEvent.location}
              onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />

            {/* Participants */}
            {availableParticipants.length > 0 && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700">Participants (optional):</label>
                <div className="flex flex-wrap gap-2">
                  {availableParticipants.map((participant) => (
                    <label key={participant.email || participant.type} className="flex items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={selectedParticipants.includes(participant.email || participant.type)}
                        onChange={(e) => {
                          const value = participant.email || participant.type;
                          if (e.target.checked) {
                            setSelectedParticipants([...selectedParticipants, value]);
                          } else {
                            setSelectedParticipants(selectedParticipants.filter(p => p !== value));
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                      <span>{participant.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={handleCreateEvent}
              disabled={!newEvent.eventTitle || !newEvent.startDate}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Create Event
            </button>
          </div>
        )}
      </div>

      {/* Events List or Calendar */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="text-center text-gray-500 py-8">Loading schedule...</div>
        ) : events.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p>No events scheduled</p>
            <p className="text-sm mt-1">Create an event above to get started</p>
          </div>
        ) : viewMode === 'calendar' ? (
          /* Calendar View */
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h3 className="text-lg font-semibold mb-4 text-center">{getMonthName()}</h3>
            <div className="grid grid-cols-7 gap-1">
              {/* Day headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-xs font-semibold text-gray-600 py-2">
                  {day}
                </div>
              ))}
              {/* Calendar days */}
              {getCalendarDays().map((dayData, idx) => {
                const todayStr = new Date().toISOString().split('T')[0];
                const isToday = dayData && dayData.date === todayStr;
                const hasEvents = dayData && dayData.events && dayData.events.length > 0;
                
                return (
                  <div
                    key={idx}
                    className={`min-h-[100px] border-2 rounded-lg p-2 transition-colors ${
                      dayData 
                        ? hasEvents 
                          ? isToday
                            ? 'bg-green-100 border-green-400 hover:bg-green-200 shadow-md'
                            : 'bg-blue-100 border-blue-400 hover:bg-blue-200 shadow-sm'
                          : isToday
                            ? 'bg-green-50 border-green-300 hover:bg-green-100'
                            : 'bg-white border-gray-300 hover:bg-gray-50'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    {dayData && (
                      <>
                        <div className="flex items-center justify-between mb-2">
                          <div className={`text-sm font-bold ${
                            isToday
                              ? 'text-green-800'
                              : hasEvents
                                ? 'text-blue-800'
                                : 'text-gray-800'
                          }`}>
                            {dayData.day}
                          </div>
                          {hasEvents && (
                            <div className="flex items-center gap-1 bg-blue-500 text-white px-1.5 py-0.5 rounded-full">
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                              {dayData.events.length > 1 && (
                                <span className="text-xs font-bold">
                                  {dayData.events.length}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        {hasEvents && (
                          <div className="space-y-1">
                            {dayData.events.slice(0, 2).map((event) => (
                              <div
                                key={event.id}
                                className={`text-xs p-1.5 rounded truncate font-medium ${getEventTypeColor(event.event_type)}`}
                                title={event.event_title}
                              >
                                {event.event_title}
                              </div>
                            ))}
                            {dayData.events.length > 2 && (
                              <div className="text-xs text-blue-700 font-bold bg-blue-200 px-1.5 py-0.5 rounded">
                                +{dayData.events.length - 2} more
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* List View */
          <div className="space-y-4">
            {sortedDates.map((date) => (
              <div key={date} className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  {formatEventDate(date)}
                </h3>
                <div className="space-y-2">
                  {groupedEvents[date].map((event) => (
                    <div key={event.id} className="border border-gray-200 rounded-lg p-3 bg-white">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-semibold text-sm">{event.event_title}</span>
                            <span className={`px-2 py-0.5 rounded text-xs ${getEventTypeColor(event.event_type)}`}>
                              {event.event_type}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(event.status)}`}>
                              {event.status.replace('_', ' ')}
                            </span>
                          </div>
                          {event.event_description && (
                            <p className="text-xs text-gray-600 mb-1">{event.event_description}</p>
                          )}
                          <div className="text-xs text-gray-500 space-y-0.5">
                            <p>
                              {formatEventDate(event.start_date, event.start_time)}
                              {event.end_date && event.end_date !== event.start_date && (
                                <> - {formatEventDate(event.end_date, event.end_time)}</>
                              )}
                            </p>
                            {event.location && <p>📍 {event.location}</p>}
                            {event.assigned_to_name && (
                              <p>👤 Assigned to: {event.assigned_to_name}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectSchedule;

